import {
	faCalendar,
	faClock,
	faDownload,
	faEmptySet,
	faMagnifyingGlass,
	faRotate,
	faSpinner,
	faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';
import { Badge, Form, InputGroup } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useSelector } from 'react-redux';
import { tss } from 'tss-react';

import { MenuPlannerItemsBulkResponse, Translation } from 'api/menuPlanner/GetMenuPlannerItemsBulk';
import FormatedDate from 'components/desktop/_general/FormatedDate/FormatedDate';
import useCultureCode from 'hooks/redux/useCultureCode';
import useTranslation from 'hooks/useTranslation';
import { RootState } from 'reducers/rootReducer';
import { Appearance } from 'types/ShoppingCart/Appearance';
import * as Translations from 'types/_general/Translation';

interface IProps {
	menuPlans: Appearance.Type[];
	menuPlansOrders: MenuPlannerItemsBulkResponse[];
	refreshMenuPlanOrders: () => void;
}

type ItemRelativeToDate = {
	date: string;
	itemsUnderThatDateIds: { id: string; titleTranslations: Translation }[];
};

type TableDataEntry = {
	menuPlanId: string;
	menuPlanName: Translations.Type<string>;
	costCenter: string;
	lastEditedDate: string | null;
	lastEditedBy: string | null;
	location: string;
	items: { [itemId: string]: { amount: number; date: string } };
};

type HoveredArea = {
	menuPlanId: string;
	date: string;
	itemId: string;
};

const ReportModalContent = (props: IProps) => {
	const { classes, cx } = useStyles();
	const cultureCode = useCultureCode();
	const { tl, t } = useTranslation();
	const reduxUser = useSelector((state: RootState) => state.user);

	const [hoveredArea, setHoveredArea] = useState<HoveredArea>({
		date: '',
		itemId: '',
		menuPlanId: '',
	});
	const [searchTerm, setSearchTerm] = useState<string>('');

	function getCsvData() {
		// Build the flat item headers with their date for column order
		const flatItemHeaders = itemsRelativeToDate.flatMap((entry) =>
			entry.itemsUnderThatDateIds.map((item) => ({
				id: item.id,
				date: entry.date,
				title: tl(item.titleTranslations),
			}))
		);

		// 1. First header row: Dates (empty for MenuPlan, CostCenter), then repeated per item
		const dateHeaderRow = [
			'',
			'',
			'',
			...itemsRelativeToDate.flatMap((entry) =>
				entry.itemsUnderThatDateIds.map(() =>
					new Date(entry.date).toLocaleDateString(cultureCode, {
						weekday: 'long',
						day: '2-digit',
						month: '2-digit',
					})
				)
			),
		];

		// 2. Second header row: Item names
		const itemHeaderRow = [
			t('menuPlan:MENU_PLANS'),
			t('shoppingCart:COST_CENTER'),
			t('shoppingCart:LAST_SUBMISSION'),
			...flatItemHeaders.map((item) => item.title),
		];

		// 3. Data rows
		const dataRows = tableData.map((mp) => {
			const base = [
				mp.location == 'Not found'
					? props.menuPlans.find((a) => a.id == mp.menuPlanId)
						? tl(props.menuPlans.find((a) => a.id == mp.menuPlanId)?.name || {})
						: mp.location
					: mp.location,
				mp.costCenter,
				Object.values(mp.items).length > 0 &&
				Object.values(mp.items).every((item) => item.amount === 0)
					? t('shoppingCart:NO_SUBMISSION_YET')
					: mp.lastEditedDate
					? new Date(mp.lastEditedDate).toLocaleString(cultureCode, {
							day: '2-digit',
							month: '2-digit',
							year: 'numeric',
							hour: '2-digit',
							minute: '2-digit',
							hour12: false,
					  }) + (mp.lastEditedBy ? ` - ${mp.lastEditedBy}` : '')
					: '',
			];
			const itemAmounts = flatItemHeaders.map((item) => {
				const record = mp.items[`${item.id}_${item.date.split('T')[0]}`];
				return record && record.date === item.date ? Number(record.amount) : 0;
			});
			return [...base, ...itemAmounts];
		});

		return [dateHeaderRow, itemHeaderRow, ...dataRows];
	}

	const { menuPlans, menuPlansOrders } = props;

	const lowerSearch = searchTerm.trim().toLowerCase();

	const menuPlanNames = Object.fromEntries(menuPlans.map((mp) => [mp.id, mp.name]));

	const itemsByDate: Record<string, { id: string; titleTranslations: Translation }[]> = {};
	menuPlansOrders.forEach((mpo) =>
		mpo.items.forEach((item) => {
			const dayDate = item.date.split('T')[0];
			if (!itemsByDate[dayDate]) itemsByDate[dayDate] = [];
			if (!itemsByDate[dayDate].some((existing) => existing.id === item.id)) {
				itemsByDate[dayDate].push({
					id: item.id,
					titleTranslations: item.titleTranslations,
				});
			}
		})
	);

	const itemsRelativeToDate: ItemRelativeToDate[] = Object.entries(itemsByDate)
		.map(([date, itemsUnderThatDateIds]) => ({ date, itemsUnderThatDateIds }))
		.sort((a, b) => a.date.localeCompare(b.date));

	const tableData: TableDataEntry[] = menuPlansOrders.map((mpo) => ({
		menuPlanId: mpo.menuplanId,
		menuPlanName: menuPlanNames[mpo.menuplanId] || '',
		costCenter: mpo.costCenter,
		items: mpo.items.reduce(
			(acc, it) => {
				const dayDate = it.date.split('T')[0];
				const key = `${it.id}_${dayDate}`;
				if (!acc[key]) {
					acc[key] = { amount: it.quantity, date: dayDate };
				} else {
					acc[key].amount += it.quantity;
				}
				return acc;
			},
			{} as { [key: string]: { amount: number; date: string } }
		),
		location: mpo.location,
		lastEditedDate: mpo.lastEditedDate ?? '',
		lastEditedBy: mpo.lastEditedBy ?? '',
	}));

	const { filteredItemsRelativeToDate, filteredTableData } = useMemo(() => {
		const filteredMenuPlanIds = menuPlans
			.filter((mp) => {
				const nameStr = typeof mp.name === 'string' ? mp.name : tl(mp.name);
				return (
					nameStr.toLowerCase().includes(lowerSearch) ||
					(menuPlansOrders.find((a) => a.menuplanId == mp.id)?.location &&
						menuPlansOrders
							.find((a) => a.menuplanId == mp.id)
							?.location.toLowerCase()
							.includes(lowerSearch))
				);
			})
			.map((mp) => mp.id);

		const filteredMenuPlansOrders = menuPlansOrders.filter((mpo) =>
			filteredMenuPlanIds.includes(mpo.menuplanId)
		);

		const menuPlanNames = Object.fromEntries(menuPlans.map((mp) => [mp.id, mp.name]));
		const filteredTableData: TableDataEntry[] = filteredMenuPlansOrders.map((mpo) => ({
			menuPlanId: mpo.menuplanId,
			menuPlanName: menuPlanNames[mpo.menuplanId] || '',
			costCenter: mpo.costCenter,
			items: mpo.items.reduce(
				(acc, it) => {
					const dayDate = it.date.split('T')[0];
					const key = `${it.id}_${dayDate}`;
					if (!acc[key]) {
						acc[key] = { amount: it.quantity, date: dayDate };
					} else {
						acc[key].amount += it.quantity;
					}
					return acc;
				},
				{} as { [key: string]: { amount: number; date: string } }
			),
			location: mpo.location,
			lastEditedDate: mpo.lastEditedDate ?? '',
			lastEditedBy: mpo.lastEditedBy ?? '',
		}));

		const filteredItemsByDate: Record<
			string,
			{ id: string; titleTranslations: Translation }[]
		> = {};
		filteredMenuPlansOrders.forEach((mpo) =>
			mpo.items.forEach((item) => {
				const dayDate = item.date.split('T')[0];
				if (!filteredItemsByDate[dayDate]) filteredItemsByDate[dayDate] = [];
				if (!filteredItemsByDate[dayDate].some((existing) => existing.id === item.id)) {
					filteredItemsByDate[dayDate].push({
						id: item.id,
						titleTranslations: item.titleTranslations,
					});
				}
			})
		);
		const filteredItemsRelativeToDate: ItemRelativeToDate[] = Object.entries(
			filteredItemsByDate
		)
			.map(([date, itemsUnderThatDateIds]) => ({ date, itemsUnderThatDateIds }))
			.sort((a, b) => a.date.localeCompare(b.date));

		return {
			filteredItemsRelativeToDate,
			filteredTableData,
		};
	}, [menuPlans, menuPlansOrders, searchTerm, tl]);

	const [refreshing, setRefreshing] = useState(false);

	const handleRefresh = async () => {
		setRefreshing(true);
		await props.refreshMenuPlanOrders();
		setRefreshing(false);
	};

	return (
		<div className={classes.container}>
			{props.menuPlans.length == 0 ? (
				<div>{t('shoppingCart:NO_SOURCES')}</div>
			) : (
				<div>
					<InputGroup style={{ marginBottom: '1rem' }}>
						<InputGroup.Text id="basic-addon1">
							<FontAwesomeIcon icon={faMagnifyingGlass}></FontAwesomeIcon>
						</InputGroup.Text>
						<Form.Control
							placeholder={t('menuPlan:SEARCH_MENUPLAN')}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
						<button
							className="btn btn-outline-primary"
							onClick={handleRefresh}
							disabled={refreshing}
						>
							<FontAwesomeIcon
								icon={refreshing ? faSpinner : faRotate}
								spin={refreshing}
							/>{' '}
							{t('shoppingCart:REFRESH')}
						</button>
						<CSVLink data={getCsvData()} filename="report.csv">
							<button className={cx(classes.csvButton, 'btn btn-primary')}>
								<FontAwesomeIcon icon={faDownload}></FontAwesomeIcon>{' '}
								{t('_general:DOWNLOAD')}
							</button>
						</CSVLink>
					</InputGroup>

					<div style={{ overflowX: 'auto', width: '100%', maxHeight: '36rem' }}>
						<table
							className="table table-striped table-bordered"
							onMouseLeave={() =>
								setHoveredArea({ date: '', itemId: '', menuPlanId: '' })
							}
						>
							<thead>
								<tr>
									<th className={classes.thStyle} style={{ zIndex: 2 }}></th>
									<th></th>
									<th></th>
									<th
										style={{
											minWidth: '2px',
											backgroundColor: 'black',
											padding: '0',
										}}
									></th>
									{filteredItemsRelativeToDate.map((dateOccurences) => (
										<React.Fragment key={dateOccurences.date}>
											<th
												className={classes.centeredText}
												colSpan={
													dateOccurences.itemsUnderThatDateIds.length
												}
												style={{
													backgroundColor: '#fafafa',
												}}
											>
												<FontAwesomeIcon
													icon={faCalendar}
													style={{ marginRight: '1rem' }}
												/>
												{new Date(dateOccurences.date).toLocaleDateString(
													cultureCode,
													{
														weekday: 'long',
														day: '2-digit',
														month: '2-digit',
													}
												)}
											</th>
											<th
												style={{
													minWidth: '2px',
													backgroundColor: 'black',
													padding: '0',
												}}
											></th>
										</React.Fragment>
									))}
								</tr>
								<tr>
									<th className={classes.thStyle} style={{ zIndex: 2 }}>
										{t('menuPlan:MENU_PLANS')}
									</th>
									<th
										onMouseEnter={() => {
											setHoveredArea({
												date: '',
												itemId: 'costCenter',
												menuPlanId: '',
											});
										}}
										className={
											hoveredArea.itemId == 'costCenter'
												? `table-primary ${classes.centeredText}`
												: classes.centeredText
										}
										style={{ minWidth: '10rem' }}
									>
										{t('shoppingCart:COST_CENTER')}
									</th>
									<th
										onMouseEnter={() => {
											setHoveredArea({
												date: '',
												itemId: 'lastSubmission',
												menuPlanId: '',
											});
										}}
										className={
											hoveredArea.itemId == 'lastSubmission'
												? `table-primary ${classes.centeredText}`
												: classes.centeredText
										}
										style={{ minWidth: '10rem' }}
									>
										<FontAwesomeIcon
											style={{
												marginRight: '0.2rem',
											}}
											icon={faClock}
										></FontAwesomeIcon>
										{t('shoppingCart:LAST_SUBMISSION')}
									</th>
									<th
										style={{
											minWidth: '2px',
											backgroundColor: 'black',
											padding: '0',
										}}
									></th>
									{filteredItemsRelativeToDate.map((item, i) => (
										<React.Fragment key={i}>
											{item.itemsUnderThatDateIds.map((appearance) => (
												<th
													key={item.date + appearance.id}
													style={{
														fontWeight: 'normal',
													}}
													onMouseEnter={() => {
														setHoveredArea({
															date: item.date,
															itemId: appearance.id,
															menuPlanId: '',
														});
													}}
													className={
														hoveredArea.itemId == appearance.id &&
														hoveredArea.date == item.date
															? `table-primary ${classes.centeredText}`
															: classes.centeredText
													}
												>
													{tl(appearance.titleTranslations)}
												</th>
											))}
											<th
												style={{
													minWidth: '2px',
													backgroundColor: 'black',
													padding: '0',
												}}
											></th>
										</React.Fragment>
									))}
								</tr>
							</thead>
							<tbody>
								{filteredTableData.map((mp) => (
									<tr key={mp.menuPlanId}>
										<td
											className={
												hoveredArea.menuPlanId == mp.menuPlanId
													? `table-primary ${classes.thStyle}`
													: classes.thStyle
											}
											style={{ zIndex: 2 }}
											onMouseEnter={() => {
												setHoveredArea({
													date: '',
													itemId: '',
													menuPlanId: mp.menuPlanId,
												});
											}}
										>
											{reduxUser.firstName == mp.location ? (
												<>
													{mp.location} |{' '}
													{tl(
														props.menuPlans.find(
															(a) => a.id == mp.menuPlanId
														)?.name || {}
													)}
												</>
											) : (
												<>
													{!mp.location.includes('Not found')
														? mp.location
														: tl(
																props.menuPlans.find(
																	(a) => a.id == mp.menuPlanId
																)?.name || {}
														  )}
												</>
											)}
										</td>

										<td
											onMouseEnter={() => {
												setHoveredArea({
													date: '',
													itemId: 'costCenter',
													menuPlanId: mp.menuPlanId,
												});
											}}
											className={
												hoveredArea.itemId == 'costCenter' ||
												hoveredArea.menuPlanId == mp.menuPlanId
													? `table-primary ${classes.centeredText}`
													: classes.centeredText
											}
											style={{ minWidth: '10rem' }}
										>
											{mp.costCenter}
										</td>
										<td
											onMouseEnter={() => {
												setHoveredArea({
													date: '',
													itemId: 'lastSubmission',
													menuPlanId: mp.menuPlanId,
												});
											}}
											className={
												hoveredArea.itemId == 'lastSubmission' ||
												hoveredArea.menuPlanId == mp.menuPlanId
													? `table-primary ${classes.centeredText}`
													: classes.centeredText
											}
											style={{ minWidth: '10rem' }}
										>
											{mp.items &&
											Object.values(mp.items).length > 0 &&
											Object.values(mp.items).every(
												(item) => item.amount === 0
											) ? (
												<Badge
													bg="warning"
													style={{
														marginRight: '1rem',
														backgroundColor: 'rgb(255 156 107)',
														display: 'flex',
														alignItems: 'center',
													}}
												>
													<FontAwesomeIcon
														style={{
															marginRight: '0.2rem',
														}}
														icon={faTriangleExclamation}
													></FontAwesomeIcon>
													{t('shoppingCart:NO_SUBMISSION_YET')}
												</Badge>
											) : (
												<Badge
													style={{
														marginRight: '1rem',
														textAlign: 'center',
													}}
												>
													{mp.lastEditedDate && (
														<span>
															<FormatedDate
																date={mp.lastEditedDate}
															></FormatedDate>{' '}
															- {mp.lastEditedBy}
														</span>
													)}
												</Badge>
											)}
										</td>
										<td
											style={{
												minWidth: '2px',
												backgroundColor: 'black',
												padding: '0',
											}}
										></td>
										{filteredItemsRelativeToDate.map((item, j) => (
											<React.Fragment key={j}>
												{item.itemsUnderThatDateIds.map((appearance, i) => (
													<td
														className={
															hoveredArea.menuPlanId ==
																mp.menuPlanId ||
															(hoveredArea.itemId == appearance.id &&
																hoveredArea.date == item.date)
																? `table-primary ${classes.centeredText}`
																: classes.centeredText
														}
														key={mp.menuPlanId + i + j}
														onMouseEnter={() => {
															setHoveredArea({
																date: item.date,
																itemId: appearance.id,
																menuPlanId: mp.menuPlanId,
															});
														}}
													>
														{mp.items[
															`${appearance.id}_${
																item.date.split('T')[0]
															}`
														] ? (
															mp.items[
																`${appearance.id}_${
																	item.date.split('T')[0]
																}`
															].amount
														) : (
															<FontAwesomeIcon
																style={{
																	color: 'gray',
																	opacity: 0.3,
																}}
																icon={faEmptySet}
															/>
														)}
													</td>
												))}
												<td
													style={{
														minWidth: '2px',
														backgroundColor: 'black',
														padding: '0',
													}}
												></td>
											</React.Fragment>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</div>
	);
};

export default ReportModalContent;

const useStyles = tss.create({
	container: {
		height: '40rem',
		minWidth: '100%',
	},
	centeredText: {
		minWidth: '15rem',
		textAlign: 'center',
	},
	thStyle: {
		position: 'sticky',
		left: 0,
		minWidth: '10rem',
		textAlign: 'center',
	},
	csvButton: {
		marginLeft: '1rem',
	},
});
